/**
 * City Agency Landing Pages - Extended Cities
 * Adds: Dubai, Lisbon, Amsterdam, Warsaw, Istanbul, Stockholm, Milan, São Paulo, Mumbai, Tokyo
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web apps, portals, dashboards. React, TypeScript, cloud-native.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'NLP, computer vision, predictive analytics, LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP. CI/CD, containers, monitoring, managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA, DevOps integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, design systems.' },
  { name: 'Cybersecurity', description: 'Pen testing, security audits, SOC, compliance (ISO 27001, GDPR, HIPAA).' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, omnichannel solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations.' },
  { name: 'SaaS & Technology', description: 'Product development and scaling for SaaS companies.' },
  { name: 'Real Estate & PropTech', description: 'Property management, virtual tours, tenant portals.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse systems.' },
];

export const CITY_AGENCY_PAGES_EXTENDED: Record<string, CityAgencyData> = {

  dubai: {
    slug: 'software-development-agency-dubai',
    cityName: 'Dubai',
    countryName: 'UAE',
    flag: '🇦🇪',
    timezone: 'GST (GMT+4)',
    currency: 'AED/USD',
    title: {
      en: 'Software Development Agency Dubai | Call IT Dev',
      fr: 'Agence de Développement Informatique Dubaï | Call IT Dev',
      ar: 'وكالة تطوير برمجيات دبي | Call IT Dev',
      de: 'Softwareentwicklung Agentur Dubai | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Dubai, UAE. Custom SaaS, web, mobile, AI. Arabic & English teams, from $25/hr. Dubai hub + Casablanca delivery.',
      fr: 'Agence de développement informatique à Dubaï. SaaS, web, mobile, IA. Équipes arabophones et anglophones, dès 25 $/h.',
      ar: 'وكالة تطوير برمجيات في دبي. SaaS مخصص، ويب، موبايل، ذكاء اصطناعي. فرق عربية وإنجليزية، من 25 $/ساعة.',
      de: 'Softwareentwicklungsagentur in Dubai. SaaS, Web, Mobile, KI. Arabisch- und englischsprachige Teams, ab 25 $/h.',
    },
    keywords: ['software development agency Dubai', 'IT outsourcing Dubai', 'web development Dubai', 'app development UAE', 'SaaS development Dubai'],
    heroTitle: {
      en: 'Software Development Agency in Dubai',
      fr: 'Agence de Développement Informatique à Dubaï',
      ar: 'وكالة تطوير برمجيات في دبي',
      de: 'Softwareentwicklung Agentur in Dubai',
    },
    heroSubtitle: {
      en: 'Dubai hub + Casablanca delivery center. Arabic & English engineering teams. Enterprise software at competitive rates.',
      fr: 'Hub Dubaï + centre de livraison Casablanca. Équipes arabophones et anglophones.',
      ar: 'مركز دبي + مركز تسليم الدار البيضاء. فرق هندسة عربية وإنجليزية.',
      de: 'Dubai Hub + Casablanca Delivery Center. Arabisch- und englischsprachige Teams.',
    },
    whyTitle: {
      en: 'Why Dubai Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Dubaï Choisissent Call IT Dev',
      ar: 'لماذا تختار شركات دبي Call IT Dev',
      de: 'Warum Unternehmen in Dubai Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🏢', title: 'Dubai Hub', description: 'Physical presence in Dubai for meetings, workshops, and project kickoffs.' },
      { icon: '🗣️', title: 'Arabic & English Teams', description: 'Bilingual engineers for MENA and global markets.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Senior devs at $25-40/hr vs $60-100/hr locally. Same quality.' },
      { icon: '⏰', title: '3h Time Overlap', description: 'Morocco GMT+1, Dubai GMT+4. 5+ hours of daily overlap.' },
      { icon: '🛡️', title: 'DIFC Compliant', description: 'Data protection aligned with UAE and DIFC regulations.' },
      { icon: '🚀', title: 'Vision 2030 Ready', description: 'Experience with government digital transformation projects.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have an office in Dubai?', answer: 'Yes, we operate a hub in Dubai for client meetings and project coordination. Our development center is in Casablanca with 200+ engineers.' },
      { question: 'Do your engineers speak Arabic?', answer: 'Yes. We have bilingual Arabic-English engineering teams for MENA market projects.' },
      { question: 'Are you compliant with UAE data protection?', answer: 'Yes. We follow UAE Federal Data Protection Law and DIFC data protection regulations. GDPR compliant with enterprise security standards.' },
      { question: 'What is the cost comparison to Dubai?', answer: 'Senior developers in Dubai cost $60-100/hr. Our rate is $25-40/hr for equivalent skills — 50%+ savings.' },
      { question: 'Can you work on government projects?', answer: 'Yes. We have experience with government digital transformation aligned with UAE Vision 2030.' },
      { question: 'How quickly can you start?', answer: 'Team onboarding takes 2 weeks. CVs and technical interviews within 5 business days.' },
    ],
    localRate: '$60-100',
    ourRate: '$25-40',
    savings: '50%+',
  },

  lisbon: {
    slug: 'software-development-agency-lisbon',
    cityName: 'Lisbon',
    countryName: 'Portugal',
    flag: '🇵🇹',
    timezone: 'WET (GMT+0)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Lisbon | Call IT Dev',
      fr: 'Agence de Développement Informatique Lisbonne | Call IT Dev',
      pt: 'Agência Desenvolvimento Software Lisboa | Call IT Dev',
      es: 'Agencia Desarrollo Software Lisboa | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Lisbon, Portugal. Custom SaaS, web, mobile, AI. Portuguese-speaking engineers, from €20/hr. 55% savings.',
      fr: 'Agence de développement informatique pour Lisbonne. SaaS, web, mobile, IA. Ingénieurs lusophones, dès 20 €/h.',
      pt: 'Agência de desenvolvimento de software para Lisboa. SaaS, web, móvel, IA. Engenheiros lusófonos, desde 20 €/h. 55% economia.',
      es: 'Agencia de desarrollo de software para Lisboa. SaaS, web, móvil, IA. Ingenieros lusófonos, desde 20 €/h.',
    },
    keywords: ['software development agency Lisbon', 'agência desenvolvimento software Lisboa', 'IT outsourcing Portugal', 'nearshore development Lisbon'],
    heroTitle: {
      en: 'Software Development Agency for Lisbon',
      fr: 'Agence de Développement Informatique pour Lisbonne',
      pt: 'Agência de Desenvolvimento de Software para Lisboa',
    },
    heroSubtitle: {
      en: 'Portuguese-speaking engineers at nearshore rates. 1-hour time difference, 55% savings vs Lisbon agencies.',
      fr: 'Ingénieurs lusophones à tarifs nearshore. 1h de décalage, 55% d\'économies.',
      pt: 'Engenheiros lusófonos a preços nearshore. 1h de diferença horária, 55% economia vs agências de Lisboa.',
    },
    whyTitle: { en: 'Why Lisbon Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Lisbonne Choisissent Call IT Dev', pt: 'Por Que Empresas de Lisboa Escolhem a Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Portuguese Engineers', description: 'Native Portuguese-speaking developers for seamless communication.' },
      { icon: '⏰', title: '1h Time Difference', description: 'Morocco GMT+1, Lisbon GMT+0. Nearly identical working hours.' },
      { icon: '💰', title: '55% Savings', description: 'Senior devs at €20-35/hr vs €50-80/hr in Lisbon.' },
      { icon: '✈️', title: '1.5h Flight', description: 'Casablanca-Lisbon: 1.5-hour flight with frequent connections.' },
      { icon: '🌍', title: 'Brazil Market Access', description: 'Portuguese-speaking teams for LATAM expansion.' },
      { icon: '🛡️', title: 'RGPD Compliant', description: 'Enterprise security standards, fully RGPD/GDPR compliant.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Os vossos engenheiros falam português?', answer: 'Sim, temos engenheiros lusófonos nativos. Toda a comunicação, documentação e reuniões em português.' },
      { question: 'How does nearshore compare to Lisbon?', answer: 'Senior developers in Lisbon cost €50-80/hr. Our rate is €20-35/hr — 55% savings with same timezone.' },
      { question: 'Can you support Brazilian market expansion?', answer: 'Yes! Portuguese-speaking teams serve both Portuguese and Brazilian markets.' },
      { question: 'Are you RGPD compliant?', answer: 'Yes. Enterprise security standards, fully compliant with Portuguese RGPD/CNPD requirements.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks. CVs within 5 business days.' },
      { question: 'What is the Casablanca-Lisbon advantage?', answer: '1.5h flight, 1h time difference, cultural proximity, and shared linguistic heritage.' },
    ],
    localRate: '€50-80',
    ourRate: '€20-35',
    savings: '55%+',
  },

  amsterdam: {
    slug: 'software-development-agency-amsterdam',
    cityName: 'Amsterdam',
    countryName: 'Netherlands',
    flag: '🇳🇱',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Amsterdam | Call IT Dev',
      fr: 'Agence de Développement Informatique Amsterdam | Call IT Dev',
      nl: 'Softwareontwikkeling Bureau Amsterdam | Call IT Dev',
      de: 'Softwareentwicklung Agentur Amsterdam | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Amsterdam. Custom SaaS, web, mobile, AI. English & Dutch speaking engineers, CET, from €25/hr. 55% savings.',
      fr: 'Agence de développement informatique pour Amsterdam. SaaS, web, mobile, IA. Ingénieurs anglophones, dès 25 €/h.',
      nl: 'Softwareontwikkelingsbureau voor Amsterdam. SaaS, web, mobiel, AI. Engels- en Nederlandstalige ingenieurs, vanaf 25 €/u. 55% besparing.',
      de: 'Softwareentwicklungsagentur für Amsterdam. SaaS, Web, Mobile, KI. Ab 25 €/h. 55% Ersparnis.',
    },
    keywords: ['software development agency Amsterdam', 'IT outsourcing Amsterdam', 'web development Netherlands', 'app development Amsterdam', 'softwareontwikkeling Amsterdam'],
    heroTitle: { en: 'Software Development Agency for Amsterdam', fr: 'Agence de Développement Informatique pour Amsterdam', nl: 'Softwareontwikkeling Bureau voor Amsterdam' },
    heroSubtitle: { en: 'English-speaking engineers at nearshore rates. Same CET timezone, 55% savings vs Amsterdam agencies.', fr: 'Ingénieurs anglophones à tarifs nearshore. Même fuseau CET, 55% d\'économies.', nl: 'Engelstalige ingenieurs tegen nearshore-prijzen. Zelfde CET-tijdzone, 55% besparing.' },
    whyTitle: { en: 'Why Amsterdam Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises d\'Amsterdam Choisissent Call IT Dev', nl: 'Waarom Amsterdamse Bedrijven Call IT Dev Kiezen' },
    whyReasons: [
      { icon: '🗣️', title: 'English-Fluent Engineers', description: 'All developers speak fluent English — the business language of Amsterdam.' },
      { icon: '⏰', title: 'Same CET Timezone', description: 'Morocco and Amsterdam share CET. Full-day collaboration.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €25-40/hr vs €70-100/hr in Amsterdam.' },
      { icon: '✈️', title: '3.5h Direct Flight', description: 'Casablanca-Amsterdam direct flights available.' },
      { icon: '🚀', title: 'Startup Ecosystem', description: 'Experience with Amsterdam\'s thriving startup and scale-up ecosystem.' },
      { icon: '🛡️', title: 'AVG/GDPR Compliant', description: 'ISO 27001, fully compliant with Dutch AVG/GDPR.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do your engineers speak English?', answer: 'Yes, all engineers are fluent English speakers (C1/C2). Dutch is not required but available for select projects.' },
      { question: 'How does nearshore compare to Amsterdam?', answer: 'Senior developers in Amsterdam cost €70-100/hr. Our rate is €25-40/hr — 55% savings, same timezone.' },
      { question: 'Are you AVG/GDPR compliant?', answer: 'Yes. Enterprise security standards, fully compliant with Dutch AVG and EU GDPR.' },
      { question: 'Can you work with Dutch startups?', answer: 'Yes! We work with seed to Series B startups from Amsterdam. Flexible engagement, no long-term lock-in.' },
      { question: 'What is the minimum engagement?', answer: 'Start with 2 developers, scale to 30+. Monthly billing, cancel anytime.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks. CVs and interviews within 5 business days.' },
    ],
    localRate: '€70-100',
    ourRate: '€25-40',
    savings: '55%+',
  },

  warsaw: {
    slug: 'software-development-agency-warsaw',
    cityName: 'Warsaw',
    countryName: 'Poland',
    flag: '🇵🇱',
    timezone: 'CET (GMT+1)',
    currency: 'PLN/EUR',
    title: {
      en: 'Software Development Agency Warsaw | Call IT Dev',
      fr: 'Agence de Développement Informatique Varsovie | Call IT Dev',
      pl: 'Agencja Rozwoju Oprogramowania Warszawa | Call IT Dev',
      de: 'Softwareentwicklung Agentur Warschau | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Warsaw, Poland. Custom SaaS, web, mobile, AI. CET timezone, from €20/hr. 45% savings vs Warsaw.',
      fr: 'Agence de développement informatique pour Varsovie. SaaS, web, mobile, IA. Fuseau CET, dès 20 €/h.',
      pl: 'Agencja rozwoju oprogramowania dla Warszawy. SaaS, web, mobile, AI. Strefa CET, od 20 €/h. 45% oszczędności.',
      de: 'Softwareentwicklungsagentur für Warschau. SaaS, Web, Mobile, KI. CET-Zeitzone, ab 20 €/h.',
    },
    keywords: ['software development agency Warsaw', 'IT outsourcing Warsaw', 'agencja software Warszawa', 'nearshore development Poland'],
    heroTitle: { en: 'Software Development Agency for Warsaw', fr: 'Agence de Développement Informatique pour Varsovie', pl: 'Agencja Rozwoju Oprogramowania dla Warszawy' },
    heroSubtitle: { en: 'CET-aligned engineers at competitive rates. 45% savings vs Warsaw agencies.', fr: 'Ingénieurs en fuseau CET à tarifs compétitifs. 45% d\'économies.', pl: 'Inżynierowie w strefie CET w konkurencyjnych cenach. 45% oszczędności vs agencje warszawskie.' },
    whyTitle: { en: 'Why Warsaw Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Varsovie Choisissent Call IT Dev', pl: 'Dlaczego Firmy z Warszawy Wybierają Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: 'Same CET Timezone', description: 'Full day overlap with Warsaw for Agile sprints.' },
      { icon: '💰', title: '45% Cost Savings', description: 'Competitive rates even compared to Polish nearshore.' },
      { icon: '🗣️', title: 'English-Fluent Teams', description: 'All communication in English with technical precision.' },
      { icon: '🎓', title: 'Engineering Excellence', description: '200+ engineers with European education standards.' },
      { icon: '🛡️', title: 'RODO/GDPR Compliant', description: 'Enterprise security standards, Polish RODO compliant.' },
      { icon: '📈', title: 'Scalable Teams', description: 'Scale from 2 to 50+ developers quickly.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Warsaw rates?', answer: 'Senior devs in Warsaw cost €40-60/hr. Our rate is €20-35/hr — 45% savings with same CET timezone.' },
      { question: 'Are you RODO/GDPR compliant?', answer: 'Yes. Enterprise security standards, fully compliant with Polish RODO and EU GDPR.' },
      { question: 'Do you work with Polish startups?', answer: 'Yes. Flexible engagement models for startups at any stage.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP, Docker, Kubernetes.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers, scale as needed.' },
    ],
    localRate: '€40-60',
    ourRate: '€20-35',
    savings: '45%+',
  },

  istanbul: {
    slug: 'software-development-agency-istanbul',
    cityName: 'Istanbul',
    countryName: 'Turkey',
    flag: '🇹🇷',
    timezone: 'TRT (GMT+3)',
    currency: 'TRY/USD',
    title: {
      en: 'Software Development Agency Istanbul | Call IT Dev',
      fr: 'Agence de Développement Informatique Istanbul | Call IT Dev',
      tr: 'Yazılım Geliştirme Ajansı İstanbul | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Istanbul, Turkey. Custom SaaS, web, mobile, AI. Turkish & English teams, from $20/hr.',
      fr: 'Agence de développement informatique pour Istanbul. SaaS, web, mobile, IA. Équipes turcophones, dès 20 $/h.',
      tr: 'İstanbul için yazılım geliştirme ajansı. SaaS, web, mobil, yapay zeka. Türkçe ve İngilizce ekipler, 20 $/saatten.',
    },
    keywords: ['software development agency Istanbul', 'IT outsourcing Istanbul', 'yazılım geliştirme İstanbul', 'nearshore development Turkey'],
    heroTitle: { en: 'Software Development Agency for Istanbul', fr: 'Agence de Développement pour Istanbul', tr: 'İstanbul İçin Yazılım Geliştirme Ajansı' },
    heroSubtitle: { en: 'Turkish & English engineers. 2h time difference, competitive rates vs Istanbul agencies.', fr: 'Ingénieurs turcophones et anglophones. 2h de décalage, tarifs compétitifs.', tr: 'Türkçe ve İngilizce mühendisler. 2 saat fark, İstanbul ajanslarına göre rekabetçi fiyatlar.' },
    whyTitle: { en: 'Why Istanbul Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises d\'Istanbul Choisissent Call IT Dev', tr: 'İstanbul Şirketleri Neden Call IT Dev\'i Seçiyor' },
    whyReasons: [
      { icon: '🗣️', title: 'Turkish & English Teams', description: 'Bilingual engineering teams for Turkish and global markets.' },
      { icon: '⏰', title: '2h Time Difference', description: 'Close timezone for real-time collaboration.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Senior devs at $20-35/hr.' },
      { icon: '🌍', title: 'MENA + Europe Bridge', description: 'Teams serving both European and Middle Eastern markets.' },
      { icon: '🛡️', title: 'KVKK/GDPR Compliant', description: 'Turkish KVKK and EU GDPR compliant.' },
      { icon: '📈', title: 'Scalable', description: 'From 2 to 50+ developers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do your engineers speak Turkish?', answer: 'Yes. Bilingual Turkish-English engineering teams available.' },
      { question: 'Are you KVKK compliant?', answer: 'Yes. We comply with Turkish KVKK and EU GDPR.' },
      { question: 'How does pricing compare to Istanbul?', answer: 'Senior devs in Istanbul cost $40-60/hr. Our rate is $20-35/hr.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'Can you support MENA expansion?', answer: 'Yes. Arabic and Turkish teams for MENA market projects.' },
    ],
    localRate: '$40-60',
    ourRate: '$20-35',
    savings: '45%+',
  },

  stockholm: {
    slug: 'software-development-agency-stockholm',
    cityName: 'Stockholm',
    countryName: 'Sweden',
    flag: '🇸🇪',
    timezone: 'CET (GMT+1)',
    currency: 'SEK/EUR',
    title: {
      en: 'Software Development Agency Stockholm | Call IT Dev',
      fr: 'Agence de Développement Informatique Stockholm | Call IT Dev',
      sv: 'Mjukvaruutveckling Byrå Stockholm | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Stockholm. Custom SaaS, web, mobile, AI. CET timezone, from €25/hr. 60% savings vs Stockholm.',
      fr: 'Agence de développement informatique pour Stockholm. SaaS, web, mobile, IA. Fuseau CET, dès 25 €/h.',
      sv: 'Mjukvaruutvecklingsbyrå för Stockholm. SaaS, webb, mobil, AI. CET-tidszon, från 25 €/h. 60% besparing.',
    },
    keywords: ['software development agency Stockholm', 'IT outsourcing Stockholm', 'mjukvaruutveckling Stockholm', 'nearshore development Sweden'],
    heroTitle: { en: 'Software Development Agency for Stockholm', fr: 'Agence de Développement pour Stockholm', sv: 'Mjukvaruutveckling Byrå för Stockholm' },
    heroSubtitle: { en: 'English-fluent engineers. Same CET timezone, 60% savings vs Stockholm agencies.', fr: 'Ingénieurs anglophones. Même fuseau CET, 60% d\'économies.', sv: 'Engelsktalande ingenjörer. Samma CET-tidszon, 60% besparing vs Stockholmbyråer.' },
    whyTitle: { en: 'Why Stockholm Companies Choose Call IT Dev', sv: 'Varför Stockholmsföretag Väljer Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'English-Fluent Engineers', description: 'All communication in English — Sweden\'s business language.' },
      { icon: '⏰', title: 'Same CET Timezone', description: 'Full overlap for Agile collaboration.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at €25-40/hr vs €80-120/hr in Stockholm.' },
      { icon: '🚀', title: 'Nordic Startup Experience', description: 'Experience with Scandinavian tech companies.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'ISO 27001, fully GDPR compliant.' },
      { icon: '📈', title: 'Scale Fast', description: 'Team setup in 2 weeks, scale to 30+.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Stockholm?', answer: 'Senior devs in Stockholm cost €80-120/hr. Our rate is €25-40/hr — 60% savings, same CET timezone.' },
      { question: 'Do you work with Swedish startups?', answer: 'Yes. Flexible engagement for Nordic startups at any stage.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes. Enterprise security standards, fully GDPR compliant.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What is the timezone overlap?', answer: 'Morocco and Sweden share CET. Full 8-hour overlap.' },
      { question: 'What technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP, Docker, K8s.' },
    ],
    localRate: '€80-120',
    ourRate: '€25-40',
    savings: '60%+',
  },

  milan: {
    slug: 'software-development-agency-milan',
    cityName: 'Milan',
    countryName: 'Italy',
    flag: '🇮🇹',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Milan | Call IT Dev',
      fr: 'Agence de Développement Informatique Milan | Call IT Dev',
      it: 'Agenzia Sviluppo Software Milano | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Milan, Italy. Custom SaaS, web, mobile, AI. Italian-speaking engineers, CET, from €20/hr. 55% savings.',
      fr: 'Agence de développement informatique pour Milan. SaaS, web, mobile, IA. Ingénieurs italophones, dès 20 €/h.',
      it: 'Agenzia di sviluppo software per Milano. SaaS personalizzato, web, mobile, AI. Ingegneri italofoni, da 20 €/h. 55% risparmio.',
    },
    keywords: ['software development agency Milan', 'agenzia sviluppo software Milano', 'IT outsourcing Milan', 'nearshore development Italy'],
    heroTitle: { en: 'Software Development Agency for Milan', fr: 'Agence de Développement pour Milan', it: 'Agenzia di Sviluppo Software per Milano' },
    heroSubtitle: { en: 'Italian-speaking engineers at nearshore rates. Same CET timezone, 55% savings vs Milan.', fr: 'Ingénieurs italophones à tarifs nearshore. 55% d\'économies.', it: 'Ingegneri italofoni a prezzi nearshore. Stesso fuso CET, 55% risparmio vs Milano.' },
    whyTitle: { en: 'Why Milan Companies Choose Call IT Dev', it: 'Perché le Aziende di Milano Scelgono Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Italian-Speaking Engineers', description: 'Developers fluent in Italian and English.' },
      { icon: '⏰', title: 'Same CET Timezone', description: 'Full overlap with Milan working hours.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €20-35/hr vs €60-90/hr in Milan.' },
      { icon: '✈️', title: '3h Flight', description: 'Casablanca-Milan direct flights available.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'ISO 27001, fully GDPR compliant.' },
      { icon: '🏭', title: 'Made in Italy Quality', description: 'Italian engineering standards applied.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'I vostri ingegneri parlano italiano?', answer: 'Sì, abbiamo ingegneri italofoni. Tutta la comunicazione in italiano o inglese.' },
      { question: 'How does nearshore compare to Milan?', answer: 'Senior devs in Milan cost €60-90/hr. Our rate is €20-35/hr — 55% savings.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes. ISO 27001, Garante Privacy compliant.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'Can you work with Italian startups?', answer: 'Yes. Flexible engagement models.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers.' },
    ],
    localRate: '€60-90',
    ourRate: '€20-35',
    savings: '55%+',
  },

  'sao-paulo': {
    slug: 'software-development-agency-sao-paulo',
    cityName: 'São Paulo',
    countryName: 'Brazil',
    flag: '🇧🇷',
    timezone: 'BRT (GMT-3)',
    currency: 'BRL/USD',
    title: {
      en: 'Software Development Agency São Paulo | Call IT Dev',
      fr: 'Agence de Développement Informatique São Paulo | Call IT Dev',
      pt: 'Agência Desenvolvimento Software São Paulo | Call IT Dev',
      es: 'Agencia Desarrollo Software São Paulo | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving São Paulo, Brazil. Custom SaaS, web, mobile, AI. Portuguese-speaking engineers, from $20/hr.',
      fr: 'Agence de développement informatique pour São Paulo. SaaS, web, mobile, IA. Ingénieurs lusophones, dès 20 $/h.',
      pt: 'Agência de desenvolvimento de software para São Paulo. SaaS, web, móvel, IA. Engenheiros lusófonos, desde 20 $/h.',
      es: 'Agencia de desarrollo de software para São Paulo. SaaS, web, móvil, IA. Ingenieros lusófonos, desde 20 $/h.',
    },
    keywords: ['software development agency São Paulo', 'agência desenvolvimento software São Paulo', 'IT outsourcing Brazil', 'nearshore development Brazil'],
    heroTitle: { en: 'Software Development Agency for São Paulo', pt: 'Agência de Desenvolvimento de Software para São Paulo' },
    heroSubtitle: { en: 'Portuguese-speaking engineers for the Brazilian market. Competitive rates, quality delivery.', pt: 'Engenheiros lusófonos para o mercado brasileiro. Preços competitivos, entrega de qualidade.' },
    whyTitle: { en: 'Why São Paulo Companies Choose Call IT Dev', pt: 'Por Que Empresas de São Paulo Escolhem a Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Portuguese Engineers', description: 'Native Portuguese-speaking developers.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Senior devs at $20-35/hr.' },
      { icon: '🌍', title: 'LATAM Expertise', description: 'Experience with Brazilian and LATAM markets.' },
      { icon: '🛡️', title: 'LGPD Compliant', description: 'Brazilian LGPD and GDPR compliant.' },
      { icon: '📈', title: 'Scalable Teams', description: 'Scale from 2 to 50+ developers.' },
      { icon: '🚀', title: 'Fast Setup', description: 'Team operational in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Os engenheiros falam português brasileiro?', answer: 'Sim, temos engenheiros lusófonos que atendem ao mercado brasileiro.' },
      { question: 'Are you LGPD compliant?', answer: 'Yes. We comply with Brazilian LGPD and EU GDPR.' },
      { question: 'What is the time difference?', answer: 'Morocco GMT+1, São Paulo GMT-3. 4-hour difference with 4+ hours daily overlap.' },
      { question: 'How does pricing compare?', answer: 'Senior devs in São Paulo cost $30-50/hr. Our rate is $20-35/hr.' },
      { question: 'Can you work with Brazilian startups?', answer: 'Yes. Flexible engagement for startups at any stage.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
    ],
    localRate: '$30-50',
    ourRate: '$20-35',
    savings: '40%+',
  },

  mumbai: {
    slug: 'software-development-agency-mumbai',
    cityName: 'Mumbai',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR/USD',
    title: {
      en: 'Software Development Agency Mumbai | Call IT Dev',
      fr: 'Agence de Développement Informatique Mumbai | Call IT Dev',
    },
    description: {
      en: 'Premium alternative to Mumbai IT outsourcing. CET-aligned delivery, European quality standards. Custom SaaS, web, mobile, AI, from $25/hr.',
      fr: 'Alternative premium au développement IT Mumbai. Livraison en fuseau CET, standards européens. SaaS, web, mobile, IA, dès 25 $/h.',
    },
    keywords: ['software development agency Mumbai alternative', 'IT outsourcing Mumbai alternative', 'nearshore vs offshore India', 'premium development alternative India'],
    heroTitle: { en: 'Premium Alternative to Mumbai IT Outsourcing', fr: 'Alternative Premium au Développement IT Mumbai' },
    heroSubtitle: { en: 'CET-aligned delivery vs IST offset. European quality standards, GDPR compliance. The nearshore advantage over offshore.', fr: 'Livraison en fuseau CET vs décalage IST. Standards européens, conformité RGPD.' },
    whyTitle: { en: 'Why Choose Nearshore Over Mumbai Offshore', fr: 'Pourquoi Choisir le Nearshore Plutôt que l\'Offshore à Mumbai' },
    whyReasons: [
      { icon: '⏰', title: 'CET Timezone', description: 'Full-day overlap with Europe vs 4.5h offset with IST.' },
      { icon: '🛡️', title: 'GDPR Native', description: 'Built-in GDPR compliance vs retrofit approach.' },
      { icon: '🗣️', title: 'Cultural Proximity', description: 'European business culture and communication style.' },
      { icon: '✈️', title: 'Easy Visits', description: '3-4h flights to major European cities.' },
      { icon: '📋', title: 'Quality Standards', description: 'European engineering standards and QA processes.' },
      { icon: '💰', title: 'Competitive Pricing', description: 'Nearshore rates from $25/hr — quality over rock-bottom pricing.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How do you compare to Mumbai IT companies?', answer: 'We offer CET timezone alignment (vs 4.5h offset), native GDPR compliance, and European quality standards at competitive nearshore rates of $25-40/hr.' },
      { question: 'What is the timezone advantage?', answer: 'Morocco GMT+1 gives full-day overlap with Europe. Mumbai IST (GMT+5:30) creates a 4.5h gap that limits real-time collaboration.' },
      { question: 'Is nearshore more expensive than offshore?', answer: 'Slightly higher hourly rates ($25-40 vs $15-25), but lower total cost when factoring in fewer bugs, faster communication, and reduced management overhead.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes. Enterprise security standards, native GDPR compliance — not retrofitted.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks. Much faster than typical offshore ramp-up.' },
      { question: 'What about code quality?', answer: 'Mandatory code reviews, 80%+ test coverage, CI/CD, SonarQube. European engineering standards.' },
    ],
    localRate: '$15-25',
    ourRate: '$25-40',
    savings: 'CET + Quality',
  },

  tokyo: {
    slug: 'software-development-agency-tokyo',
    cityName: 'Tokyo',
    countryName: 'Japan',
    flag: '🇯🇵',
    timezone: 'JST (GMT+9)',
    currency: 'JPY/USD',
    title: {
      en: 'Software Development Agency Tokyo | Call IT Dev',
      fr: 'Agence de Développement Informatique Tokyo | Call IT Dev',
      ja: 'ソフトウェア開発会社 東京 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Tokyo. Custom SaaS, web, mobile, AI. Cost-effective nearshore alternative, from $25/hr. 65% savings.',
      fr: 'Agence de développement informatique pour Tokyo. SaaS, web, mobile, IA. Alternative nearshore, dès 25 $/h. 65% d\'économies.',
      ja: '東京向けソフトウェア開発会社。SaaS、ウェブ、モバイル、AI。ニアショア代替、25$/時間〜。65%コスト削減。',
    },
    keywords: ['software development agency Tokyo', 'IT outsourcing Tokyo', 'nearshore development Japan', 'ソフトウェア開発 東京'],
    heroTitle: { en: 'Software Development Agency for Tokyo', ja: '東京向けソフトウェア開発会社' },
    heroSubtitle: { en: 'Cost-effective nearshore alternative to Tokyo agencies. 65% savings, international quality standards.', ja: '東京の代理店に対するコスト効率の高いニアショア代替。65%のコスト削減。' },
    whyTitle: { en: 'Why Tokyo Companies Choose Call IT Dev', ja: '東京の企業がCall IT Devを選ぶ理由' },
    whyReasons: [
      { icon: '💰', title: '65% Cost Savings', description: 'Senior devs at $25-40/hr vs $80-120/hr in Tokyo.' },
      { icon: '🗣️', title: 'English Communication', description: 'All communication in English with documentation.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'International security and quality standards.' },
      { icon: '📈', title: 'Scalable Teams', description: 'From 2 to 50+ developers.' },
      { icon: '🚀', title: 'Fast Delivery', description: 'Agile methodology, 2-week sprints.' },
      { icon: '🌍', title: 'Global Experience', description: '200+ clients across 15+ countries.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How do you handle the timezone difference?', answer: 'Morocco GMT+1, Tokyo GMT+9. We offer 2-3 hours of daily overlap and async workflows with daily progress reports and documentation.' },
      { question: 'Do your engineers speak Japanese?', answer: 'Communication is in English. For Japanese-language projects, we can provide bilingual project managers.' },
      { question: 'How does pricing compare to Tokyo?', answer: 'Senior devs in Tokyo cost $80-120/hr. Our rate is $25-40/hr — 65% savings.' },
      { question: 'Are you ISO certified?', answer: 'Yes. We follow enterprise security standards with international quality practices.' },
      { question: 'How quickly can you start?', answer: 'Team setup in 2 weeks.' },
      { question: 'What technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP, Docker, Kubernetes.' },
    ],
    localRate: '$80-120',
    ourRate: '$25-40',
    savings: '65%+',
  },
};
